import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { connectToHubBooking } from "src/redux/slices/hub";
import {
  upsertBookingLocal,
  removeBookingLocal,
  fetchUpcomingBookings,
  fetchBookingAlerts,
} from "src/redux/slices/bookings";
import buildHubUrl from "./buildHubUrl";
import CreateConnection from "./HubMatrix";
import { addReconnectListener } from "./HubConnectionManager";
import { showNotificationBoundToThisTab } from "../notifications";
import MyAutoSnack from "src/components/MyAutoSnack";

// Hub de reservas — única fuente de cambios en tiempo real. Sin polling.
//
// Estrategia hub-driven (cero refetches redundantes):
//   - bootstrap del TPV trae la lista inicial de `upcoming` (limit 30).
//   - bookings/created  → upsertBookingLocal (el reducer inserta si es futura).
//   - bookings/changed  → upsertBookingLocal (merge + saca del subset si el
//     status pasó a Terminal Cancelled/NoShow/Completed).
//   - bookings/deleted  → removeBookingLocal.
//   - fetchBookingAlerts SÍ se llama: los flags SeenByRestaurant vienen del
//     servidor, no del payload del hub.
//
// Al reconectar el websocket (posibles eventos perdidos durante la caída),
// hacemos UN único refetch con force:true del subset upcoming + alerts para
// recuperar consistencia. Sin caídas, no hay peticiones adicionales.
//
// IMPORTANTE — Status enum del backend (BookingStatus):
//   0 PendingApproval, 1 Confirmed, 2 Seated, 3 NoShow, 4 Cancelled, 5 Completed

export default function useHubBooking() {
  const dispatch = useDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { user } = useSelector((state) => state.authJwt);

  // Guardia local del hook (mismo patrón que useHubMesas) — evita doble
  // conexión bajo Strict Mode / cambios de ruta. El flag de Redux no se
  // resetea al cerrar el websocket, así que confiamos en el ref local.
  const isConnectedRef = useRef(false);

  useEffect(() => {
    if (!user?.id || isConnectedRef.current) return;

    isConnectedRef.current = true;

    const hubUrl = buildHubUrl("/hub/bookinghub", { restauranteId: user.id });

    // Al reconectar tras una caída del websocket (posible pérdida de eventos
    // Create/Change/Delete), refrescamos una sola vez: upcoming (widget TPV)
    // + alerts (bell TopBar) + lista completa si la vista /reservas está
    // montada — cubre el hueco sin generar tráfico en operación normal.
    const unsubscribeReconnect = addReconnectListener(hubUrl, () => {
      dispatch(fetchUpcomingBookings({ limit: 30, force: true }));
      dispatch(fetchBookingAlerts({ force: true }));
    });

    const cleanup = CreateConnection(
      hubUrl,
      [
        {
          id: `booking/created/${user.id}`,
          operator: async (booking) => {
            if (!booking) return;
            // El reducer ya inserta la reserva en `upcoming` si es futura y
            // status vivo — no hace falta refetch. Alerts sí: SeenByRestaurant
            // se calcula server-side y no viene en el payload del hub.
            dispatch(upsertBookingLocal(booking));
            dispatch(fetchBookingAlerts({ force: true }));

            const isPendingApproval = booking.status === 0;
            const title = isPendingApproval
              ? "Nueva reserva pendiente de confirmar"
              : "Nueva reserva";
            const body = formatBookingToastBody(booking);

            MyAutoSnack(
              isPendingApproval ? "warning" : "info",
              `${title} — ${body}`,
              enqueueSnackbar,
              closeSnackbar,
            );

            // Notificación nativa del navegador (incluso si la pestaña no
            // está enfocada). Usa el helper showNotificationBoundToThisTab
            // que ya gestiona permisos y deduplicación por tag.
            await showNotificationBoundToThisTab({
              title,
              body,
              url: `/app/tucarta/reservas`,
              tag: `booking-${booking.id}`,
            });
          },
        },
        {
          id: `booking/changed/${user.id}`,
          operator: async (booking) => {
            if (!booking) return;
            // El reducer sincroniza `upcoming` (merge o salida del subset si
            // pasa a estado terminal Cancelled/NoShow/Completed).
            dispatch(upsertBookingLocal(booking));
            dispatch(fetchBookingAlerts({ force: true }));

            // Alerta explícita SOLO para cancelaciones (status 4). El resto
            // de cambios son silenciosos. Una reserva cancelada (típicamente
            // por el cliente desde la web) debe avisarse: snackbar + push
            // nativo, además de la notificación persistente que crea el
            // backend en la campanita del TopBar.
            if (booking.status === 4) {
              const body = formatBookingToastBody(booking);

              MyAutoSnack(
                "warning",
                `Reserva cancelada — ${body}`,
                enqueueSnackbar,
                closeSnackbar,
              );

              await showNotificationBoundToThisTab({
                title: "Reserva cancelada",
                body,
                url: `/app/tucarta/reservas`,
                tag: `booking-cancel-${booking.id}`,
              });
            }

            // No-show (status 3): típicamente lo marca el auto-no-show del
            // webjob al pasar la hora + cortesía sin sentarse. Avisamos al
            // restaurante igual que una cancelación (snackbar + push). El
            // backend ya la dejó SeenByRestaurant=false → destaca en el centro.
            if (booking.status === 3) {
              const body = formatBookingToastBody(booking);

              MyAutoSnack(
                "warning",
                `Reserva marcada como ausente — ${body}`,
                enqueueSnackbar,
                closeSnackbar,
              );

              await showNotificationBoundToThisTab({
                title: "Reserva ausente (no-show)",
                body,
                url: `/app/tucarta/reservas`,
                tag: `booking-noshow-${booking.id}`,
              });
            }
          },
        },
        {
          id: `booking/deleted/${user.id}`,
          operator: (idBooking) => {
            if (idBooking == null) return;
            // removeBookingLocal ya limpia bookings + upcoming en un solo pase.
            dispatch(removeBookingLocal(idBooking));
          },
        },
      ],
      dispatch,
      connectToHubBooking,
      "booking",
    );

    return () => {
      unsubscribeReconnect();
      cleanup();
      isConnectedRef.current = false;
    };
  }, [user?.id, dispatch, enqueueSnackbar]);
}

function formatBookingToastBody(b) {
  const name = b?.name || "Cliente";
  const pax = b?.number ? `${b.number} pax` : "";
  let when = "";
  try {
    const d = new Date(b?.date);
    if (!Number.isNaN(d.getTime())) {
      when = d.toLocaleString("es-ES", {
        weekday: "short",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  } catch (_) {}
  return [name, pax, when].filter(Boolean).join(" · ");
}
